import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, Typography, Avatar, Grid, Divider, Container } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Slider from 'react-slick';
import styled from 'styled-components';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useSpring, animated } from 'react-spring';
import './Testimonials.css';
import { Footer } from '../Footer';
import { NavbarPage } from '../Navbar/Navbar';

// Styled components
const StyledCard = styled(Card)`
  background: linear-gradient(135deg, #e2a2ff, #ffc1f4);
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  text-align: center;
  margin-bottom: 40px;
`;

const TopMargin = styled.div`
  margin-top: 100px;
`;

const TestimonialCard = styled(Card)`
  background: linear-gradient(135deg, #e2a2ff, #ffc1f4);
  margin: 20px;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: translateY(-10px);
  }
`;

const TestimonialContent = styled(CardContent)`
  padding: 16px;
`;

const TestimonialAvatar = styled(Avatar)`
  width: 90px;
  height: 90px;
  margin: 0 auto 20px;
  border: 5px solid #fff;
`;

const TestimonialText = styled(Typography)`
  margin-bottom: 12px;
  font-style: italic;
  color: #555;
  text-align: center;
`;

const settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  centerMode: true,
  centerPadding: '0',
  fade: true, // Add fade effect for smoother transition
  arrows: true, // Show arrows
};

// Full list of testimonials
const testimonials = [
  'Impressive documentation! It is incredibly easy to navigate, with clear instructions and intuitive design, making it accessible even for beginners in API integration.',
  'The real-time API status updates have been a game-changer, helping us prevent potential integration issues before they escalate into problems, ensuring smooth operation at all times.',
  'The API solutions are highly scalable, allowing us to easily adapt to the growth of our business. Whether we’re handling small or large-scale operations, it fits perfectly for businesses of any size.',
  'With robust security features, this API platform ensures that our integrations are always safe and secure, giving us peace of mind when handling sensitive data.',
  'In-depth API metrics provide valuable insights that help us optimize our performance. The detailed analytics allow us to monitor usage and make data-driven decisions to improve efficiency.',
  'Seamless integration with our existing systems and workflows has saved us countless hours of development time, letting us focus on scaling and improving our services rather than dealing with technical complexities.',
  'Customer support is top-notch! Their team is always responsive, professional, and helpful, providing quick solutions whenever we encounter any issues.',
  'This API solution is cost-effective while still offering premium features, which makes it a great value for money. We can access advanced functionalities without breaking the budget.',
  'The customizable API settings allow us to fine-tune the integration to meet our specific needs, providing a tailored experience that maximizes our workflow efficiency.',
  "We've tried several API platforms, but this one stands out as the best due to its ease of integration and performance. It's simple to set up and consistently delivers excellent results.",
  'The user-friendly interface makes it easy for our team to get up and running with minimal setup time, reducing the learning curve and allowing us to focus on our core business activities.',
  'Reliable API performance statistics are invaluable for tracking uptime and understanding system behavior, helping us identify potential bottlenecks and optimize performance.',
  'The advanced analytics provided by the platform help us make smarter decisions about our API usage, ensuring maximum efficiency and minimal waste, which leads to a more cost-effective operation.',
  'Transparent pricing with no hidden fees or unexpected charges ensures that we always know exactly what to expect when it comes to costs, making budgeting and planning much easier.',
  'Efficient resource allocation and optimized API calls have led to significant improvements in our performance. We’re now using our resources more effectively, resulting in faster, more responsive systems.',
  'The intelligent alert system proactively informs us of any potential issues or downtime, allowing us to address problems before they affect our services or users.',
  'We highly recommend this platform for developers and IT professionals due to its comprehensive features and ease of use. It simplifies even the most complex integration tasks.',
  'The comprehensive reporting tools provide detailed insights that are perfect for monitoring and audits, ensuring compliance and giving us full visibility into our system’s performance.',
  'Cloud-ready API solutions ensure our infrastructure is future-proof, allowing us to scale and integrate new technologies as they emerge without worrying about compatibility or limitations.',
  'This API platform has truly been a game-changer in our approach to API management. It’s exceeded our expectations, providing all the features we need and more to optimize our operations.',
];

// Shuffle function to randomize the array
const shuffleArray = (array) => {
  const shuffledArray = [...array]; // Use `const` as it's not reassigned
  for (let i = shuffledArray.length - 1; i > 0; i -= 1) {
    // Replace -- with i -= 1
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

function TestimonialPage() {
  const springProps = useSpring({ opacity: 1, from: { opacity: 0 } });
  const AnimatedTestimonialCard = animated(TestimonialCard);

  // Randomly select 5 testimonials for the slider
  const randomTestimonials = shuffleArray(testimonials).slice(0, 5);

  return (
    <>
      <Helmet>
        <title>Testimonial | InnoWave API Hub</title>
      </Helmet>
      <NavbarPage />
      <TopMargin />
      <CssBaseline />
      <StyledCard>
        <main>
          <Container>
            <AnimatedTestimonialCard>
              <div className="testimonials-container">
                <h2
                  style={{
                    textAlign: 'center',
                    marginBottom: '30px',
                    color: '#333333',
                    padding: '10px',
                  }}
                >
                  &lsquo; What our customers say &rsquo;
                </h2>
                <div className="slider-container">
                  <Slider {...settings}>
                    {randomTestimonials.map((testimonial, index) => (
                      <div key={index} className="testimonial-item">
                        <TestimonialAvatar alt="User" src={`/assets/images/avatars/gender/avatar_${index + 25}.jpg`} />
                        <p>{testimonial}</p>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
            </AnimatedTestimonialCard>
          </Container>
          <br />
          <Divider />
          <Grid container justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <AnimatedTestimonialCard style={springProps}>
                  <TestimonialAvatar alt="User" src={`/assets/images/avatars/gender/avatar_${index + 1}.jpg`} />
                  <TestimonialContent>
                    <TestimonialText variant="body1" color="textPrimary">
                      "{testimonial}"
                    </TestimonialText>
                  </TestimonialContent>
                </AnimatedTestimonialCard>
              </Grid>
            ))}
          </Grid>
        </main>
      </StyledCard>
      <div style={{ position: 'relative', bottom: 0, width: '100%' }}>
        <Footer />
      </div>
    </>
  );
}

export default TestimonialPage;
